import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';
import * as ReactDOM from 'react-dom';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { useQuery, ApolloProvider } from '@apollo/client';
import { store } from 'store';
import {
	EditablePolygon,
	GeometryData,
	Layer,
	Library,
	PointData,
	PolygonBlockedStatus,
	PolygonData,
	TileCoords,
} from 'interfaces/maps.interface';
import Loader from 'sharedComponents/Loader';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
	center,
	constants,
	libraries,
	mapContainerStyle,
	MapTypesID,
} from 'utils/constants';
import useSeasonalConfigs from 'customHooks/useSeasonalConfigs';
import useApolloClientAndMsalAuth from 'customHooks/useApolloClientAndMsalAuth';
import {
	closeCurrentBrowserTab,
	getUIClient,
	latLngConversion,
} from 'utils/helpers';

import deriveSelectedLayerGeometryType from 'utils/deriveSelectedLayerGeometryType';
import {
	ParentPlaceIdTag,
	PlaceTag,
	PointGeometry,
	PolygonGeometry,
	ReferenceAttribute,
} from 'interfaces/vector.interface';
import getPlaceTagValue from 'utils/getPlaceTagValue';
import {
	calculateApprovedGeofenceAcres,
	calculateCurrentFarmGeofencedArea,
	calculatePendingGeofenceAcres,
	calculateRejectedGeofenceAcres,
	resetAWDStore,
	setEditablePolygons,
	setPointMapTiles,
	setPointsOfFarmId,
	setPolygonMapTiles,
	setSelectedFarmData,
	// setSelectedPoint,
} from 'reducers/awdView.reducer';
import TileUnit from 'sharedMapComponents/TileUnit';
import { GET_PLACES_BY_TILES } from 'GraphQL/queries/mapService';
import { useSearchParams } from 'customHooks/useSearchParams';
import { redirect } from 'actions/navigation';
import debounce from 'utils/debounce';
import { GET_CONTENT_DETAILS_BY_FARM_ID } from 'GraphQL/queries/contentService';
import {
	setSnackbarMessage,
	setSnackbarSeverity,
} from 'reducers/global.reducer';
import { GeofenceStatus, PointMetaData } from 'interfaces/awd.interface';
import DrawingContainer from 'modules/MapView/components/GoogleMapView/sections/DrawingContainer';
import PolygonUnit from 'sharedMapComponents/PolygonUnit';
import MarkerUnit from 'sharedMapComponents/MarkerUnit';
import AlertModal from 'widgets/AlertModal';
import {
	ALL_LAYERS,
	TOTAL_SUGGESTIONS_ALLOWED,
} from 'modules/SugarcaneMapView/constants';

export const mapOptions = {
	disableDefaultUI: false,
	draggable: true,
	zoomControl: true,
	rotateControl: true,
	streetViewControl: false,
	fullscreenControl: false,
	keyboardShortcuts: false,
	mapTypeControl: false,
	disableDoubleClickZoom: true,
	mapTypeId: 'hybrid',
	mapTypeIds: [...MapTypesID],
	maxZoom: 19,
	minZoom: 15,
};

export type URLParams = {
	appName: string;
};

const getParentPointSignatures = (
	tags: PlaceTag[],
	parentPointPropertyKey: string,
) => {
	let parentPointSignatures: string[] = [];
	const parentPointPlaceTagValues: ParentPlaceIdTag[] = getPlaceTagValue(
		tags,
		'PARENT',
		parentPointPropertyKey,
		'VALUE_TYPE_NSV_PLACE_ID',
	) as ParentPlaceIdTag[];
	parentPointPlaceTagValues.forEach((el) => {
		parentPointSignatures.push(
			`${el.placeId}-${el.PlaceNamespace}-${el.PlaceSourceType}-${el.PlaceVisibleType}`,
		);
	});
	return parentPointSignatures;
};

interface GoogleMapViewProps {
	addToPointPlaceIdsOfFarmIdRef: (a: number[]) => void;
	addToPolygonPlaceIdsOfFarmIdRef: (a: number[]) => void;
	handleClick?: (e: google.maps.MapMouseEvent) => void;
	handleDblClick?: (e: google.maps.MapMouseEvent) => void;
	handleZoomChange: () => void;
	mapsRef: any;
	pointMapTilesRef: any;
	pointPlaceIdsOfFarmIdRef: any;
	polygonMapTilesRef: any;
	polygonPlaceIdsOfFarmIdRef: any;
	setSmartPolygonSuggestionEnabled: (a: Record<string, number>) => void;
	tilesPlaceIdsRef: any;
	updateGoogleRef: (a: typeof google.maps) => void;
	updateSuggestionGeometriesRef: (a: Record<string, GeometryData>) => void;
	zoom: number;
}

const GoogleMapView = ({
	addToPointPlaceIdsOfFarmIdRef,
	addToPolygonPlaceIdsOfFarmIdRef,
	handleClick,
	handleDblClick,
	handleZoomChange,
	mapsRef,
	pointMapTilesRef,
	pointPlaceIdsOfFarmIdRef,
	polygonMapTilesRef,
	polygonPlaceIdsOfFarmIdRef,
	setSmartPolygonSuggestionEnabled,
	tilesPlaceIdsRef,
	updateGoogleRef,
	updateSuggestionGeometriesRef,
	zoom,
}: GoogleMapViewProps): JSX.Element => {
	const searchParams = useSearchParams();
	const dispatch = useAppDispatch();
	const { client } = useApolloClientAndMsalAuth();

	const pointsOfFarmId = useAppSelector((s) => s.awdView.pointsOfFarmId);
	const polygonMapTiles = useAppSelector((s) => s.awdView.polygonMapTiles);
	const pointMapTiles = useAppSelector((s) => s.awdView.pointMapTiles);

	const { isLoaded } = useLoadScript({
		googleMapsApiKey: `${constants('GOOGLE_MAPS_API_KEY')}`,
		libraries: libraries as Library[],
	});

	const { appName } = useParams<URLParams>();
	let lat = useAppSelector((s) => s.router.location.query.lat);
	let lng = useAppSelector((s) => s.router.location.query.lng);
	lat = ['', null].includes(lat) ? center.lat : Number(lat);
	lng = ['', null].includes(lng) ? center.lng : Number(lng);
	const farmId = useAppSelector((s) => s.router.location.query.farmId);
	const eventId = useAppSelector((s) => s.router.location.query.eventId);

	const [
		{
			dataLayers,
			suggestionLayers,
			filters,
			geofenceStatusPropertyKey,
			geofenceRejectionReasonsPropertyKey,
			parentPointPropertyKey,
			portalLockPropertyKey,
			programNames,
		},
		seasonId,
		seasonKey,
	] = useSeasonalConfigs();
	const [isCursorOverPoint, setIsCursorOverPoint] = useState<boolean>(false);
	const [isDeletedFarmAlertVisible, setIsDeletedFarmAlertVisible] =
		useState<boolean>(false);

	const polygonMapTilesList = useMemo(
		() => Object.values(polygonMapTiles),
		[polygonMapTiles],
	);
	const pointMapTilesList = useMemo(
		() => Object.values(pointMapTiles),
		[pointMapTiles],
	);
	const pointsOfFarmIdList = useMemo(
		() => Object.values(pointsOfFarmId),
		[pointsOfFarmId],
	);

	const { loading, refetch } = useQuery(GET_CONTENT_DETAILS_BY_FARM_ID, {
		fetchPolicy: 'network-only',
		variables: {
			input: {
				farmId: farmId,
				seasonFilter: seasonKey,
				appName:
					appName === 'sugarcane'
						? 'APP_NAME_SUGARCANE'
						: 'APP_NAME_SUGARCANE',
			},
		},
		client: client,
		skip: farmId === null,
		onCompleted: (data) => {
			if (
				data?.getContentDetailsV3?.ResponseStatus?.status !== 'SUCCESS'
			) {
				dispatch(
					setSnackbarMessage(
						'Something went wrong. Please try reloading the page!',
					),
				);
				dispatch(setSnackbarSeverity('error'));
				if (
					data?.getContentDetailsV3?.ResponseStatus?.status ===
						'BAD_INPUT' &&
					data?.getContentDetailsV3?.ResponseStatus?.statusReason ===
						'FARM_DATA_NOT_FOUND'
				) {
					setIsDeletedFarmAlertVisible(true);
				}
				return;
			}
			let res = data?.getContentDetailsV3;

			if (res === undefined) {
				dispatch(
					setSnackbarMessage(
						'No points found for the season in this location!!',
					),
				);
				dispatch(setSnackbarSeverity('error'));
				return;
			}
			const {
				placeContentDetails,
				placeFarmDetails,
				farmPlaceIds,
				contentPlaceIds,
				parentFarmMeta,
			} = res;

			let contentPayload: Record<string, PointMetaData> = {};
			let editablePolygonsPayload: Record<string, EditablePolygon> = {};
			let smartPolygonSuggestionMap: Record<string, number> = {};
			let referralPhoneNo = '';
			// let totalPlots = 0;

			// CMSResponse
			placeContentDetails.forEach((el: any) => {
				const { place, placeContentMeta } = el;
				const key = `${placeContentMeta?.placeId}-${placeContentMeta?.PlaceNamespace}-${placeContentMeta?.PlaceSourceType}-${placeContentMeta?.PlaceVisibleType}`;
				if (
					['INSIDE_BUND_SRC', 'PIPE_INSTALLATION_SRC'].includes(
						placeContentMeta?.PlaceSourceType,
					) &&
					programNames.includes(placeContentMeta?.programName)
				) {
					const geometry = latLngConversion(place.geometry)
						.geometry as PointGeometry;
					referralPhoneNo = placeContentMeta?.referralPhoneNo;
					// totalPlots += 1;
					// if (placeContentMeta?.contentId === Number(contentId)) {
					// 	dispatch(setSelectedPoint(key));
					// }
					contentPayload[key] = {
						contentId: placeContentMeta?.contentId,
						geometry,
						farmId: farmId!,
						imageUrl: placeContentMeta?.contentURL,
						imageApprovalStatus: placeContentMeta?.approvalStatus,
						placeId: placeContentMeta?.placeId,
						timestamp: new Date(
							Number(placeContentMeta?.timestamp?.seconds) * 1000,
						).toLocaleString([], {
							weekday: 'short',
							year: 'numeric',
							month: 'long',
							day: 'numeric',
						}),
						accuracy: placeContentMeta?.accuracy,
						eventType: placeContentMeta?.PlaceSourceType,
						geofencingStatus: placeContentMeta?.geofencingStatus,
						// geofencedAcres: '0',
						imageRejectionReasons:
							placeContentMeta?.rejectionReasons,
						placeTags: place.tags,
						programName: placeContentMeta?.programName,
						referralPhoneNo: placeContentMeta?.referralPhoneNo,
						selectedLayer: {
							PlaceNamespace: placeContentMeta.PlaceNamespace,
							PlaceSourceType: placeContentMeta.PlaceSourceType,
							PlaceVisibleType: placeContentMeta.PlaceVisibleType,
						},
					};
					smartPolygonSuggestionMap[key] =
						contentPayload[key].geofencingStatus ===
						'GEOFENCING_SAVED'
							? -1
							: TOTAL_SUGGESTIONS_ALLOWED;
				}
			});
			setSmartPolygonSuggestionEnabled(smartPolygonSuggestionMap);
			// if (totalPlots === 0) {
			// 	dispatch(
			// 		setSnackbarMessage(
			// 			'No points found for the season in this location!!',
			// 		),
			// 	);
			// 	dispatch(setSnackbarSeverity('error'));
			// 	return;
			// }
			// FarmResponse
			placeFarmDetails.forEach(
				({
					place,
				}: {
					place: {
						geometry: string;
						createdTime: { seconds: number };
						placeId: string;
						tags: PlaceTag[];
						referenceAttributes: ReferenceAttribute[];
					};
				}) => {
					let parentPointSignatures: string[] = [];
					let isPolygonLocked: PolygonBlockedStatus = 'UNBLOCKED';
					let polygonApprovalStatus: GeofenceStatus =
						'GEOFENCING_PENDING';
					let currentEditablePolygonProgramName =
						'No Registered Program';
					let currentEditablePolygonRejectionReasons: string[] = [];
					const currentEditablePolygonIndex = Object.keys(
						editablePolygonsPayload,
					).length;
					const geometry = latLngConversion(place.geometry)
						.geometry as PolygonGeometry;
					if (place?.tags?.length > 0) {
						isPolygonLocked = (getPlaceTagValue(
							place.tags,
							'LOCKS',
							portalLockPropertyKey,
						)[0] ?? isPolygonLocked) as PolygonBlockedStatus;

						polygonApprovalStatus = getPlaceTagValue(
							place.tags,
							'GEOFENCE',
							geofenceStatusPropertyKey,
						)[0] as GeofenceStatus;

						parentPointSignatures = getParentPointSignatures(
							place.tags,
							parentPointPropertyKey,
						);

						currentEditablePolygonProgramName = getPlaceTagValue(
							place.tags,
							'PROGRAM',
							'NAME',
						)[0] as string;

						currentEditablePolygonRejectionReasons =
							getPlaceTagValue(
								place.tags,
								'GEOFENCE',
								geofenceRejectionReasonsPropertyKey,
							) as string[];

						place.tags.forEach((el) => {
							if (
								el.TagName === 'PARENT' &&
								el.TagPropertyKey === parentPointPropertyKey &&
								el.value.valueType === 'VALUE_TYPE_NSV_PLACE_ID'
							) {
								const parentPointPlaceTagValues =
									el.value.nsvPlaceIdValues;
								parentPointPlaceTagValues.forEach((el) => {
									let parentKey = `${el.placeId}-${el.PlaceNamespace}-${el.PlaceSourceType}-${el.PlaceVisibleType}`;
									contentPayload[
										parentKey
									].enclosingPolygonIndex = currentEditablePolygonIndex;
									parentPointSignatures.push(parentKey);
								});
							}
						});
					}
					editablePolygonsPayload[currentEditablePolygonIndex] = {
						createdTime: new Date(
							Number(place?.createdTime?.seconds) * 1000,
						).toLocaleString([], {
							weekday: 'short',
							year: 'numeric',
							month: 'long',
							day: 'numeric',
						}),
						isPolygonLocked,
						geometry,
						parentPointSignatures,
						placeId: place.placeId,
						placeTags: place.tags,
						polygonApprovalStatus,
						programName: currentEditablePolygonProgramName,
						referenceAttributes: place.referenceAttributes,
						rejectionReasons:
							currentEditablePolygonRejectionReasons,
					};
				},
			);
			addToPolygonPlaceIdsOfFarmIdRef(farmPlaceIds);
			addToPointPlaceIdsOfFarmIdRef(contentPlaceIds);
			dispatch(
				setSelectedFarmData({
					farmId,
					farmerId: parentFarmMeta?.farmerId,
					referralPhoneNo,
					registeredFarmAcres: parentFarmMeta?.acres,
				}),
			);

			dispatch(setPointsOfFarmId(contentPayload));
			dispatch(setEditablePolygons(editablePolygonsPayload));
		},
	});

	const getPlaceTagFilters = useCallback(
		(selectedLayer: Layer) => {
			const layerKey = `${selectedLayer?.PlaceNamespace}-${selectedLayer?.PlaceSourceType}-${selectedLayer?.PlaceVisibleType}`;
			return filters[layerKey];
		},
		[filters],
	);

	const shouldApplyTagFilters = useCallback(
		(selectedLayer: Layer): boolean => {
			const layerKey = `${selectedLayer?.PlaceNamespace}-${selectedLayer?.PlaceSourceType}-${selectedLayer?.PlaceVisibleType}`;
			if (suggestionLayers.includes(layerKey)) return false;
			return true;
		},
		[suggestionLayers],
	);

	const getVariables = useCallback(
		(coord: TileCoords, selectedLayer: Layer, zoom = 19) => {
			return {
				input: {
					tileRequests: {
						env: {
							sourceType: selectedLayer?.PlaceSourceType,
							namespace: selectedLayer?.PlaceNamespace,
							visibleType: selectedLayer?.PlaceVisibleType,
							client: getUIClient(),
						},
						mapTiles: [
							{
								zoom: zoom,
								tileX: coord.x,
								tileY: coord.y,
							},
						],
						tagFilterV2: shouldApplyTagFilters(selectedLayer)
							? getPlaceTagFilters(selectedLayer)
							: {},
					},
				},
			};
		},
		[getPlaceTagFilters, shouldApplyTagFilters],
	);

	const updateUrlParams = useCallback(
		(params: any) => {
			searchParams.set('lat', params.lat);
			searchParams.set('lng', params.lng);
			if (params.farmId !== undefined) {
				searchParams.set('farmId', params.farmId);
			}
			if (params.eventId !== undefined) {
				searchParams.set('eventId', params.eventId);
			}
			if (params.seasonId !== undefined) {
				searchParams.set('seasonId', params.seasonId);
			}
			const newPath = `sugarcane?${searchParams.toString()}`;
			dispatch(redirect(newPath));
		},
		[dispatch, searchParams],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const updateCenterRef = useCallback(
		debounce(() => {
			if (mapsRef.current !== null) {
				const newCenter = mapsRef.current.getCenter();
				const newLat = newCenter.lat();
				const newLng = newCenter.lng();
				// centerRef.current.lat = newLat;
				// centerRef.current.lng = newLng;
				let newParams: any = { lat: newLat, lng: newLng };
				if (!['', null].includes(farmId)) newParams.farmId = farmId;
				if (!['', null].includes(eventId)) newParams.eventId = eventId;
				if (!['', null].includes(seasonId))
					newParams.seasonId = seasonId;
				updateUrlParams(newParams);
			}
		}, 1000),
		[seasonId, farmId, eventId],
	);

	const checkForDuplicateKeysAndUpdatePayload = useCallback(
		(
			payload: Record<string, GeometryData>,
			placeData: any,
			coord: TileCoords,
			selectedLayer: Layer,
		) => {
			if (
				polygonPlaceIdsOfFarmIdRef.current.has(placeData.placeId) ||
				pointPlaceIdsOfFarmIdRef.current.has(placeData.placeId)
			) {
				return;
			}
			const { type, geometry } = latLngConversion(placeData.geometry);
			const key = `${placeData.placeId}-${selectedLayer.PlaceNamespace}-${selectedLayer.PlaceSourceType}-${selectedLayer.PlaceVisibleType}`;
			let temp: GeometryData;
			if (payload[key]) {
				temp = {
					...payload[key],
					parentTiles: new Set(payload[key].parentTiles),
				};
			} else {
				temp = {
					placeId: placeData.placeId as string,
					selectedLayer,
					placeGeometryResult: {
						geometryType: type as string,
						geometry: geometry as PolygonGeometry,
					},
					placeTags: placeData.tags as PlaceTag[],
					parentTiles: new Set(),
				};
				if (type === 'Polygon') {
					temp.parentPointSignatures = [];

					if (temp.placeTags.length > 0) {
						temp.parentPointSignatures = getParentPointSignatures(
							placeData.tags,
							parentPointPropertyKey,
						);
					}
				}
			}
			temp.parentTiles?.add(`${coord.x}-${coord.y}`);
			payload[key] = temp;
		},
		[
			parentPointPropertyKey,
			pointPlaceIdsOfFarmIdRef,
			polygonPlaceIdsOfFarmIdRef,
		],
	);

	const updateMapTilesData = useCallback(
		(mapResults: any[], selectedLayer: Layer, coord: TileCoords) => {
			let placesResult = mapResults[0]?.places;
			if (placesResult === undefined || placesResult?.length < 1) return;

			const layerKey = `${selectedLayer.PlaceNamespace}-${selectedLayer.PlaceSourceType}-${selectedLayer.PlaceVisibleType}`;
			let payload: Record<string, GeometryData> =
				deriveSelectedLayerGeometryType(selectedLayer) === 'POINT'
					? { ...pointMapTilesRef.current }
					: { ...polygonMapTilesRef.current };

			if (
				tilesPlaceIdsRef.current[`${coord.x}-${coord.y}`] === undefined
			) {
				tilesPlaceIdsRef.current[`${coord.x}-${coord.y}`] = new Set();
			}

			placesResult.forEach((placeData: any) => {
				const key = `${placeData.placeId}-${selectedLayer.PlaceNamespace}-${selectedLayer.PlaceSourceType}-${selectedLayer.PlaceVisibleType}`;
				tilesPlaceIdsRef.current[`${coord.x}-${coord.y}`].add(key);
				checkForDuplicateKeysAndUpdatePayload(
					payload,
					placeData,
					coord,
					selectedLayer,
				);
			});

			if (suggestionLayers.includes(layerKey)) {
				updateSuggestionGeometriesRef(payload);
				return;
			}

			deriveSelectedLayerGeometryType(selectedLayer) === 'POINT'
				? dispatch(
						setPointMapTiles(payload as Record<string, PointData>),
				  )
				: dispatch(
						setPolygonMapTiles(
							payload as Record<string, PolygonData>,
						),
				  );
		},
		[
			pointMapTilesRef,
			polygonMapTilesRef,
			tilesPlaceIdsRef,
			suggestionLayers,
			dispatch,
			checkForDuplicateKeysAndUpdatePayload,
			updateSuggestionGeometriesRef,
		],
	);

	const updateIsCursorOverPoint = useCallback(
		(val: boolean): void => {
			setIsCursorOverPoint(val);
		},
		[setIsCursorOverPoint],
	);

	const pruneTilesData = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		(tile: string, selectedLayer: Layer) => {
			// let tempTilePlaceIdMap = { ...tilesPlaceIdsRef.current };
			// if (!tempTilePlaceIdMap[tile]) {
			// 	return;
			// }
			// let tempMapTiles =
			// 	deriveSelectedLayerGeometryType(selectedLayer) === 'POINT'
			// 		? pointMapTilesRef.current
			// 		: polygonMapTilesRef.current;
			// tempTilePlaceIdMap[tile].forEach((el: string) => {
			// 	if (tempMapTiles && tempMapTiles[el]) {
			// 		tempMapTiles[el].parentTiles.delete(tile);
			// 		if (tempMapTiles[el].parentTiles.size === 0) {
			// 			delete tempMapTiles[el];
			// 		}
			// 	}
			// });
			// delete tempTilePlaceIdMap[tile];
			// tilesPlaceIdsRef.current = tempTilePlaceIdMap;
			// deriveSelectedLayerGeometryType(selectedLayer) === 'POINT'
			// 	? setPointMapTiles(tempMapTiles)
			// 	: setPolygonMapTiles(tempMapTiles);
		},
		[],
	);

	const onTileLoadCallCompleted = useCallback(
		(data: any, selectedLayer: Layer, coord: TileCoords): void => {
			const mapResults: any[] = data?.getPlacesByTiles?.tileResponse;
			if (mapResults?.length) {
				updateMapTilesData(mapResults, selectedLayer, coord);
			}
		},
		[updateMapTilesData],
	);

	const addLayersToMap = useCallback(
		(layers: string[]) => {
			layers.forEach((layer: string) => {
				mapsRef.current?.overlayMapTypes?.push(
					new CoordMapType(
						client,
						getVariables,
						onTileLoadCallCompleted,
						pruneTilesData,
						ALL_LAYERS[layer],
						new google.maps.Size(256, 256),
					),
				);
			});
		},
		[
			client,
			getVariables,
			mapsRef,
			onTileLoadCallCompleted,
			pruneTilesData,
		],
	);

	const onLoad = useCallback(
		(mapInstance: google.maps.Map) => {
			mapsRef.current = mapInstance;
			mapsRef.current.setCenter({ lat, lng });
			addLayersToMap([...suggestionLayers, ...dataLayers]);
		},
		[addLayersToMap, dataLayers, lat, lng, mapsRef, suggestionLayers],
	);

	const resetAndReinitializeOnSeasonUpdate = () => {
		setIsDeletedFarmAlertVisible(false);
		dispatch(resetAWDStore());
		refetch();
		if (
			mapsRef.current !== null &&
			mapsRef.current.overlayMapTypes?.length > 0
		) {
			mapsRef.current.overlayMapTypes.clear();
			addLayersToMap([...suggestionLayers, ...dataLayers]);
			mapsRef.current.overlayMapTypes.clear();
		}
	};

	useEffect(() => {
		if (isLoaded && !loading) {
			updateGoogleRef(google.maps);
			dispatch(calculateCurrentFarmGeofencedArea());
			dispatch(calculateApprovedGeofenceAcres());
			dispatch(calculatePendingGeofenceAcres());
			dispatch(calculateRejectedGeofenceAcres());
		}
	}, [dispatch, isLoaded, loading, updateGoogleRef]);

	useEffect(() => {
		polygonMapTilesRef.current = polygonMapTiles;
	}, [polygonMapTiles, polygonMapTilesRef]);

	useEffect(() => {
		pointMapTilesRef.current = pointMapTiles;
	}, [pointMapTiles, pointMapTilesRef]);

	useEffect(() => {
		// centerRef.current.lat = lat;
		// centerRef.current.lng = lng;
		mapsRef.current !== null && mapsRef.current.setCenter({ lat, lng });
		// setMapCenter({ lat, lng });
	}, [lat, lng, mapsRef]);

	useEffect(() => {
		resetAndReinitializeOnSeasonUpdate();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [seasonId]);

	return (
		<div style={{ width: '100%', height: '100%' }}>
			{!isLoaded || false ? (
				<Loader loading />
			) : (
				<>
					<GoogleMap
						// center={centerRef.current}
						// center={mapCenter}
						id="map"
						mapContainerStyle={mapContainerStyle}
						mapTypeId={google.maps.MapTypeId.HYBRID}
						options={mapOptions}
						onClick={handleClick}
						onDblClick={handleDblClick}
						onLoad={onLoad}
						onCenterChanged={updateCenterRef}
						onZoomChanged={handleZoomChange}
						zoom={zoom || 19}
					>
						<DrawingContainer />
						{polygonMapTilesList?.length > 0 &&
							polygonMapTilesList.map(
								(polygonData: PolygonData, idx: number) => (
									<PolygonUnit
										borderStoke="#FADDFF"
										colorIndex={idx}
										fillColor=""
										isCursorOverPoint={isCursorOverPoint}
										key={`${polygonData.placeId}-${polygonData.selectedLayer?.PlaceNamespace}-${polygonData.selectedLayer?.PlaceSourceType}-${polygonData.selectedLayer?.PlaceVisibleType}`}
										paths={
											polygonData?.placeGeometryResult
												?.geometry
										}
										parentPointSignatures={
											polygonData.parentPointSignatures
										}
										placeId={polygonData.placeId}
										polygonIndex={idx}
										polygonMetaData={polygonData?.placeTags}
									/>
								),
							)}
						{pointMapTilesList?.length > 0 &&
							pointMapTilesList.map((pointData: PointData) => (
								<MarkerUnit
									approvalStatus={
										pointsOfFarmId?.[
											`${pointData.placeId}-${pointData.selectedLayer?.PlaceNamespace}-${pointData.selectedLayer?.PlaceSourceType}-${pointData.selectedLayer?.PlaceVisibleType}`
										]?.imageApprovalStatus
									}
									isInteractive
									key={`${pointData.placeId}-${pointData.selectedLayer?.PlaceNamespace}-${pointData.selectedLayer?.PlaceSourceType}-${pointData.selectedLayer?.PlaceVisibleType}`}
									markerMetaData={pointData?.placeTags}
									markerType="SECONDARY"
									placeId={pointData.placeId}
									position={
										pointData?.placeGeometryResult?.geometry
									}
									selectedLayer={pointData.selectedLayer}
									showToolTipOnHover
									updateIsCursorOverPoint={
										updateIsCursorOverPoint
									}
									zoom={zoom}
								/>
							))}
						{pointsOfFarmIdList?.length > 0 &&
							pointsOfFarmIdList.map(
								(pointData: PointMetaData) => (
									<MarkerUnit
										approvalStatus={
											pointData.imageApprovalStatus
										}
										isInteractive
										key={`${pointData.placeId}-${pointData.selectedLayer.PlaceNamespace}-${pointData.selectedLayer.PlaceSourceType}-${pointData.selectedLayer.PlaceVisibleType}`}
										placeId={pointData.placeId}
										position={pointData.geometry}
										selectedLayer={pointData.selectedLayer}
										zoom={zoom}
									/>
								),
							)}
					</GoogleMap>
					<AlertModal
						heading="Farm Data unavailable"
						subHeading="The Farm you are trying to access is either INACTIVE or DELETED. Please try again!"
						buttons={[
							{
								buttonText: 'Ok, close tab',
								onClick: () => {
									setIsDeletedFarmAlertVisible(false);
									closeCurrentBrowserTab();
								},
								type: 'contained',
							},
						]}
						open={isDeletedFarmAlertVisible}
						handleClose={() => {
							setIsDeletedFarmAlertVisible(false);
						}}
					/>
				</>
			)}
		</div>
	);
};

export default React.memo(GoogleMapView);

class CoordMapType {
	/* eslint-disable */
	client: any;
	getVariables: (
		coord: TileCoords,
		selectedLayer: Layer,
		zoom: number,
	) => any;
	onTileLoadCallCompleted: (a: any, b: Layer, c: TileCoords) => void;
	pruneTilesData: (a: string, b: Layer) => void;
	selectedLayer: Layer | null;
	tileSize: google.maps.Size;
	/* eslint-enable */
	constructor(
		client: any,
		getVariables: (
			coord: TileCoords,
			selectedLayer: Layer,
			zoom: number,
		) => any,
		onTileLoadCallCompleted: (a: any, b: Layer, c: TileCoords) => void,
		pruneTilesData: (a: string, b: Layer) => void,
		selectedLayer: Layer | null,
		tileSize: google.maps.Size,
	) {
		this.tileSize = tileSize;
		this.selectedLayer = selectedLayer;
		this.onTileLoadCallCompleted = onTileLoadCallCompleted;
		this.pruneTilesData = pruneTilesData;
		this.getVariables = getVariables;
		this.client = client;
	}

	getTile(
		coord: google.maps.Point,
		zoom: number,
		ownerDocument: Document,
	): HTMLElement {
		const div = ownerDocument.createElement('div');
		ReactDOM.render(
			<ApolloProvider client={this.client}>
				<Provider store={store}>
					<TileUnit
						coord={coord}
						getVariables={this.getVariables}
						height={this.tileSize.height}
						onLoadCall={GET_PLACES_BY_TILES}
						onLoadCallCompleted={this.onTileLoadCallCompleted}
						pruneTilesData={this.pruneTilesData}
						selectedLayer={this.selectedLayer}
						width={this.tileSize.width}
						zoom={zoom}
					/>
				</Provider>
			</ApolloProvider>,
			div,
		);
		return div;
	}

	releaseTile(tile: Element): void {
		ReactDOM.unmountComponentAtNode(tile);
	}
}
